import { capitalize } from 'es-toolkit';
import { trackSiteSearch } from '@snowplow/browser-plugin-site-tracking';
import {
  FlowEnum,
  getABTestContext,
  getAgentContext,
  getAiSearchContext,
  getNavigationContext,
  getPageContext,
  getPropertyContext,
  getPropertyFilterContext,
  getSearchContext,
  getShopContext,
  getSortContext,
  type LeadIntentionsType,
  PropertyTypesEnum,
  type SelfDescribingJson,
  trackNavigationEvent,
  trackUserInteractionEvent,
  UserActionEnum,
} from '@ev-tech/snowplow-library';
import {
  CountryCode,
  type Currency,
  isSearchListingResultFull,
  isSearchListingResultRestricted,
  isSearchListingResultUserAccountExclusive,
  type Language,
  type MarketCountryCode,
  MeasurementSystem,
  PropertyMarketingType,
  PropertyType,
  type SearchFilterRequestPreferences,
  type SearchListing,
  type SearchListingResult,
  type SearchListingsFilters,
  type SearchListingsOptions,
  type SearchListingsResponse,
  type SearchRestrictedListing,
  type SearchUserAccountExclusiveListing,
} from '@ev-tech/search-modules-api';

import { getSortValues } from './stringUtils';
import { getCardPrice } from './propertyCardUtils';
import { getLatLngBoundsCenter, multiPolygonG2dToLatLngBounds } from './map';
import { getBadges, type GetBadgesParams } from './badgesUtils';
import { PageType, TemplateType } from '../types/tracking';
import { WatchlistAction, WatchlistSource } from '../providers/WatchlistProvider';
import { type TrackingConfig } from '../providers/AppContextProvider';
import { getConsentContext } from '../hooks/useSearchModuleTracking';
import { SearchMode } from '../hooks/stores/search/slices/searchModeSlice';
import { DEFAULT_BUSINESS_AREA, DEFAULT_PROPERTY_MARKETING_TYPE } from '../hooks/stores/search/slices/objectTypeFilterSlice';
import { type SortingOptionId } from '../enums/enums';
import { DEFAULT_OPTIONS } from '../consts/searchDefaults';
import { TRACKING_SEARCH_CONTEXT_CATEGORY } from '../consts/consts';
import { INTERNAL_PREFERENCE_PREFIX } from '../consts/aiSearch';

export const sendTrackSiteSearchEvent = ({
  results,
  filters,
  options,
  shopNames,
  placeName,
  inputText,
  typedInText,
  numberOfAutoCompleteOptions,
  autoCompleteOptionIndex,
  tracking,
  countryCode,
  isMapToggledOn,
  autoCompleteOptionCategory,
  searchMode,
  selectedLokaliseOptionIndex,
  sortingOptionId,
}: {
  results?: SearchListingsResponse;
  filters: SearchListingsFilters;
  options: SearchListingsOptions;
  shopNames?: string[];
  placeName?: string;
  inputText?: string;
  typedInText?: string;
  numberOfAutoCompleteOptions?: number;
  autoCompleteOptionIndex?: number;
  tracking?: TrackingConfig;
  countryCode?: string;
  isMapToggledOn?: boolean;
  autoCompleteOptionCategory?: string;
  searchMode: SearchMode;
  selectedLokaliseOptionIndex?: number;
  sortingOptionId?: SortingOptionId;
}) => {
  const isLipaPortfolioPage = filters.shopIds || filters.masterDataShopIds;

  const advisorId = filters.promotedBy || filters.agentId;

  const { currency, measurementSystem, language } = options;
  const { sortOrder, sortField } = getSortValues({
    sortingOption: filters.sortingOptions?.[0],
    sortingOptionId,
  });

  const siteSearchPayload: Parameters<typeof trackSiteSearch>[0] = {
    terms: isLipaPortfolioPage && shopNames ? (shopNames.length > 0 ? [...shopNames] : ['']) : [placeName || ''],
    filters: {
      ...mapSearchStateToFilterContext(filters, currency, measurementSystem),
    },
    totalResults: results?.listingsTotal ?? 0,
    context: [
      getSortContext({
        sortOrder,
        sortField,
      }),
      getPageContext({
        isHQPage: true,
        type: tracking?.pageType ?? (isLipaPortfolioPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT),
        page_language: language,
        country_code: countryCode,
        template_type: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
      }),
      getShopContext({
        shop_id: filters.masterDataShopIds?.[0],
        license_id: filters.masterDataLiPaIds?.[0],
        business_division_name: filters.businessArea[0] ?? DEFAULT_BUSINESS_AREA,
      }),
      getAgentContext({
        agent_id: advisorId,
        is_private_office: filters.privateOffice,
      }),
      getSearchContext({
        search_term_typed: typedInText,
        search_term_selected: searchMode === SearchMode.AI ? placeName : inputText,
        category: advisorId ? TRACKING_SEARCH_CONTEXT_CATEGORY.ADVISOR : autoCompleteOptionCategory,
        ...(results && { place_id: filters.placeIds?.join(','), postal_code: filters.postalCode }),
        ...(results &&
          filters.multiPolygon && {
            latitude: getLatLngBoundsCenter(multiPolygonG2dToLatLngBounds(filters.multiPolygon)).lat.toString(),
            longitude: getLatLngBoundsCenter(multiPolygonG2dToLatLngBounds(filters.multiPolygon)).lng.toString(),
          }),
        ...(results &&
          autoCompleteOptionCategory?.includes('natural_feature') && {
            place: 'natural_feature',
          }),
        ...(autoCompleteOptionIndex && { selected_term_position: autoCompleteOptionIndex }),
        ...(numberOfAutoCompleteOptions && { total_search_terms: numberOfAutoCompleteOptions }),
        ...(selectedLokaliseOptionIndex && { search_term_index_be: selectedLokaliseOptionIndex }),
      }),
      getPropertyFilterContext(mapSearchStateToFilterContext(filters, currency, measurementSystem)),
      getABTestContext({
        id: 'isMapToggledOn',
        name: 'isMapToggledOn',
        variant: isMapToggledOn ? 'true' : 'false',
        variant_description: 'map initially toggled on or off',
      }),
      getConsentContext(),
      getAiSearchContext(
        mapSearchStateToAiSearchContext({
          preferences: filters.preferences,
          isAiSearchEnabled: searchMode === SearchMode.AI,
        }),
      ),
    ],
  };

  trackSiteSearch(siteSearchPayload);
};

export const mapPropertyMarketingTypeToLeadContext = (
  propertyMarketingType?: PropertyMarketingType,
): { flow: FlowEnum; intention: LeadIntentionsType } => {
  return {
    flow: propertyMarketingType === PropertyMarketingType.sale ? FlowEnum.buyer : FlowEnum.rent,
    intention: propertyMarketingType === PropertyMarketingType.sale ? 'buy' : 'rent',
  };
};

export function mapSearchStateToFilterContext(
  filters: SearchListingsFilters,
  currency: Currency,
  measurementSystem: MeasurementSystem,
): Record<string, string | boolean> {
  let propertyType: string | undefined = Object.keys(filters.propertyTypeSubType ?? {}).join(',');
  const propertySubType: string | undefined = Object.values(filters.propertyTypeSubType ?? {})
    .flat()
    .join(',');

  if (propertyType === PropertyType.land) {
    propertyType = PropertyTypesEnum.plot;
  }

  const unitSuffix = measurementSystem === MeasurementSystem.metric ? 'Sqmt' : 'Sqft';

  const currencySuffix = capitalize(currency.toLowerCase());
  const context: Record<string, string | boolean> = {
    type: propertyType,
    searchIntent: filters.propertyMarketingType[0] ?? DEFAULT_PROPERTY_MARKETING_TYPE,
    subType: propertySubType,
    constructionYearMin: filters.constructionYear?.min?.toString() || '',
    constructionYearMax: filters.constructionYear?.max?.toString() || '',
    ...(filters.propertyMarketingType[0] === PropertyMarketingType.sale && {
      priceMin: filters.price?.min?.toString(),
      priceMax: filters.price?.max?.toString(),
    }),
    ...(filters.propertyMarketingType[0] === PropertyMarketingType.rent && {
      rentPriceMin: filters.price?.min?.toString(),
      rentPriceMax: filters.price?.max?.toString(),
    }),
    currency: currencySuffix,
    measurementUnit: unitSuffix.toUpperCase(),
    livingAreaMin: filters.livingSurface?.min?.toString() || '',
    livingAreaMax: filters.livingSurface?.max?.toString() || '',
    plotAreaMin: filters.plotSurface?.min?.toString() || '',
    plotAreaMax: filters.plotSurface?.max?.toString() || '',
    roomsMin: filters.rooms?.min?.toString() || '',
    roomsMax: filters.rooms?.max?.toString() || '',
    bathroomsMin: filters.bathrooms?.min?.toString() || '',
    bathroomsMax: filters.bathrooms?.max?.toString() || '',
    bedroomsMin: filters.bedrooms?.min?.toString() || '',
    bedroomsMax: filters.bedrooms?.max?.toString() || '',
    hasGarden: Boolean(filters.hasGarden),
    hasTerrace: Boolean(filters.hasTerrace),
    hasBalcony: Boolean(filters.hasBalcony),
    hasSeaview: Boolean(filters.hasSeaOrLakeView),
    hasPatio: Boolean(filters.hasPatio),
    hasPriceUpdated: Boolean(filters.hasPriceUpdated),
    hasTennisYard: Boolean(filters.hasTennisCourt),
    hasPool: Boolean(filters.hasSwimmingPool),
    hasAirconditioning: Boolean(filters.hasAirConditioning),
    hasAlarmSystem: Boolean(filters.hasSecuritySystem),
    hasCellar: Boolean(filters.hasBasement),
    hasElevator: Boolean(filters.hasElevator),
    hasKitchen: Boolean(filters.hasBuiltInKitchen),
    hasParking: Boolean(filters.hasGarage),
    postalCode: filters.postalCode || '',
    businessArea: filters.businessArea[0] ?? DEFAULT_BUSINESS_AREA,
    searchRadius: filters.searchRadius?.toString() || '0',
  };
  // remove empty values
  const ctx = Object.entries(context).reduce((acc, currentValue) => {
    const [key, value] = currentValue;
    if (value) {
      acc = {
        ...acc,
        [key]: value,
      };
    }
    return acc;
  }, {});
  return ctx;
}

export function hasUserAcceptedTracking(): boolean {
  if (!window.Didomi) {
    return false;
  }

  return window.Didomi.getLegitimateInterestStatusForVendor('c:engeland-UPm6kfxh') ?? false;
}

export const trackSearchAlertCreatedInDataLayer = ({
  placeName,
  email,
  pageType,
  templateType,
}: {
  placeName: string;
  email: string;
  pageType: PageType;
  templateType: PageType;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'search_alert_created',
    searchAlertLocation: placeName,
    templateType,
    pageType,
    ...(email && { email }),
  });
};

export const getPropertyDisturberVariants = ({
  imageDisturbers,
  countryAlpha2,
  propertyMarketingType,
}: {
  imageDisturbers: GetBadgesParams['imageDisturbers'];
  countryAlpha2?: CountryCode;
  propertyMarketingType?: PropertyMarketingType;
}): string | undefined => {
  const variants = getBadges({
    imageDisturbers,
    translationResolver: () => '',
    countryAlpha2,
    propertyMarketingType,
  }).map((badge) => badge.variant.toLowerCase());

  return variants.length > 0 ? variants.join(',') : undefined;
};

export const mapSearchListingToPropertyContext = (property: SearchListing): Parameters<typeof getPropertyContext>[0] => {
  const { area, price, baseCurrency, displayId, id, propertyType, rooms, bathrooms, bedrooms, ...rest } = property;

  const livingArea = area?.livingSurface?.min;
  const plotArea = area?.plotSurface?.min;

  return {
    propertyId: displayId,
    bffObjectId: id,
    type: propertyType || '',
    currency: baseCurrency,
    price: getCardPrice({
      listing: property,
      prices: property.basePrice ?? {},
      language: DEFAULT_OPTIONS.language,
      currency: baseCurrency ?? DEFAULT_OPTIONS.currency,
    }).value,
    propertyDisturber: getPropertyDisturberVariants({
      imageDisturbers: property.imageDisturbers,
      countryAlpha2: property.countryAlpha2 ?? CountryCode.DE,
      propertyMarketingType: property.propertyMarketingType,
    }),
    isRestricted: false,
    ...(livingArea && { livingArea: livingArea.toString() }),
    ...(plotArea && { plotArea: plotArea.toString() }),
    ...(rooms && { rooms: rooms.min?.toString() }),
    ...(bathrooms && { bathrooms: bathrooms.min?.toString() }),
    ...(bedrooms && { bedrooms: bedrooms.min?.toString() }),
    ...rest,
  };
};

export const mapRestrictedListingToPropertyContext = (listing: SearchRestrictedListing): Parameters<typeof getPropertyContext>[0] => {
  return {
    bffObjectId: listing.id,
    isRestricted: true,
  };
};

export const mapUserAccountExclusiveListingToPropertyContext = (
  listing: SearchUserAccountExclusiveListing,
): Parameters<typeof getPropertyContext>[0] => {
  return {
    bffObjectId: listing.id,
    isRestricted: false,
  };
};

export const mapSearchListingResultToPropertyContext = (listingResult: SearchListingResult): Parameters<typeof getPropertyContext>[0] => {
  if (isSearchListingResultFull(listingResult)) {
    return mapSearchListingToPropertyContext(listingResult.listing);
  }

  if (isSearchListingResultUserAccountExclusive(listingResult)) {
    return mapUserAccountExclusiveListingToPropertyContext(listingResult.userAccountExclusive);
  }

  if (isSearchListingResultRestricted(listingResult)) {
    return mapRestrictedListingToPropertyContext(listingResult.restricted);
  }

  throw new Error('SearchListingResult has no listing variant');
};

export const getWatchlistElementId = ({
  property,
  action,
  source,
}: {
  property: SearchListing;
  action: WatchlistAction;
  source: WatchlistSource;
}) => {
  const prefix = source === WatchlistSource.PREVIEW ? 'map-module_' : '';
  const listingType = property.isDevelopmentProject ? 'project' : 'expose';
  const watchlistAction = action === WatchlistAction.ADD ? 'add-to' : 'remove-from';

  return `${prefix}${listingType}_${watchlistAction}-watchlist`;
};

export const trackWatchlistToggle = ({
  property,
  action,
  source,
  language,
  countryCode,
  isSignedIn,
  tracking,
  additionalTrackingContexts = [],
}: {
  property: SearchListing;
  action: WatchlistAction;
  source: WatchlistSource;
  language: Language;
  countryCode: MarketCountryCode;
  isSignedIn: boolean;
  tracking?: TrackingConfig;
  additionalTrackingContexts?: (SelfDescribingJson | ((property: SearchListing) => SelfDescribingJson))[];
}) => {
  if (!tracking) return;

  const elementId = getWatchlistElementId({ property, action, source });

  const contexts: SelfDescribingJson[] = [
    ...additionalTrackingContexts.map((context) => (typeof context === 'function' ? context(property) : context)),
    getPageContext({
      type: tracking.pageType,
      isHQPage: true,
      template_type: tracking.templateType,
      page_language: language,
      country_code: countryCode,
    }),
    getConsentContext(),
    getPropertyContext(mapSearchListingToPropertyContext(property)),
  ];

  if (isSignedIn) {
    trackUserInteractionEvent({
      action: UserActionEnum.click,
      elementId,
      contexts,
    });
  } else {
    trackNavigationEvent({
      screenName: '',
      contexts: [
        ...contexts,
        getNavigationContext({
          element_id: elementId,
          target_screen: 'user-account_sign-in',
        }),
      ],
    });
  }
};

export type ShopChipElementId =
  | 'lipa-chip_remove'
  | 'lipa-chip_dropdown'
  | 'lipa-chip_secondary_show-all-properties'
  | 'lipa-chip-snackbar_undo';

export const trackShopChipInteraction = ({
  elementId,
  tracking,
  language,
  countryCode,
}: {
  elementId: ShopChipElementId;
  tracking: TrackingConfig;
  language: Language;
  countryCode: MarketCountryCode;
}) => {
  trackUserInteractionEvent({
    action: UserActionEnum.click,
    elementId,
    contexts: [
      getPageContext({
        type: tracking.pageType,
        isHQPage: true,
        template_type: tracking.templateType,
        page_language: language,
        country_code: countryCode,
      }),
      getConsentContext(),
    ],
  });
};

export type AiSearchChipRemovalSource = 'bar' | 'drawer';
export type AiSearchChipVariant = 'filter' | 'preference';

export const trackAiSearchChipRemoval = ({
  source,
  chipVariant,
  pageContext,
  shopContext,
}: {
  source: AiSearchChipRemovalSource;
  chipVariant: AiSearchChipVariant;
  pageContext: SelfDescribingJson;
  shopContext: SelfDescribingJson;
}) => {
  const chipType = chipVariant === 'preference' ? 'interest' : 'filter';

  trackUserInteractionEvent({
    action: UserActionEnum.click,
    elementId: `ai-chip_${chipType}_${source}_remove`,
    contexts: [pageContext, getConsentContext(), shopContext],
  });
};

export const mapSearchStateToAiSearchContext = ({
  preferences,
  isAiSearchEnabled,
  preferenceScoreOverall,
}: {
  preferences?: SearchFilterRequestPreferences;
  isAiSearchEnabled?: boolean;
  preferenceScoreOverall?: number;
}): Parameters<typeof getAiSearchContext>[0] => {
  const trackingPreferenceKeys = Object.keys(preferences ?? {}).filter(
    (preferenceKey) => !preferenceKey.startsWith(INTERNAL_PREFERENCE_PREFIX),
  );
  const numberOfPreferences = trackingPreferenceKeys.length;

  return {
    is_ai_search_enabled: isAiSearchEnabled,
    ...(isAiSearchEnabled &&
      numberOfPreferences > 0 && {
        ai_search_preferences: trackingPreferenceKeys.join(','),
        number_of_preferences: numberOfPreferences,
        preference_match_score: preferenceScoreOverall,
      }),
  };
};

export const getPrevNextTrackingString = (
  numOfSlides: number,
  previousIndex: number,
  currentIndex: number,
): 'back' | 'next' | undefined => {
  const isWrapNext = currentIndex === 0 && previousIndex === numOfSlides - 1;
  const isWrapBack = previousIndex === 0 && currentIndex === numOfSlides - 1;

  if (isWrapNext || (currentIndex === previousIndex + 1 && !isWrapBack)) {
    return 'next';
  } else if (isWrapBack || currentIndex + 1 === previousIndex) {
    return 'back';
  }
  return undefined;
};
